import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SafelistLibraryList, Tooltip, Icon, IconLookup } from 'component-library-and-tools';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@redux-requests/react';
import { isEmpty } from 'lodash-es';

import Button from 'shared/components/Button';
import LoadingWrapper from 'shared/components/LoadingWrapper/LoadingWrapper';
import {
    fetchSafelistSummaries,
    startSafelistEditing,
    migrateSafelists,
    MIGRATE_SAFELISTS,
    FETCH_SAFELIST_SUMMARIES,
} from 'services/safelists/actions';
import { getSafelistSummaryMap } from 'services/safelists/getters';
import { openModal } from 'services/modal/actions';
import { TransformationType, ScoreFilteringType, IndicatorType } from 'shared/types/transformation';
import { ToggleFeature, flags, treatments } from 'services/features';
import { canUserManageSafelists, getThemeMode } from 'services/user/getters';
import {
    ADD_SAFELIST_MODAL,
    REMOVE_SAFELIST_MODAL,
} from 'shared/constants/modalNames';
import { Safelist } from 'shared/types/safelist';
import { colors, colorsByTheme, Theme } from 'lib/prisma-styles';

import { AddSafelistModal } from '../../../safelist-detail-view/AddSafelistModal';
import { EditSafelistModal } from '../../../safelist-detail-view/EditSafelistModal';
import { RemoveSafelistModal } from '../../../safelist-detail-view/RemoveSafelistModal';

import TransformationOptionList from './TransformationOptionList';
import { TransformationSelectionProps } from './types';

const INDICATOR_TYPE_LIST_CONTENT_CONTAINER =
    'indicator-type-list__content-container';
const INDICATOR_TYPE_LIST_OPTION_WRAPPER =
    'indicator-type-list__option-wrapper';

const SAFELIST_LIBRARY_ITEM_CLASS_NAME = 'workflows-safelist-library-item';
const SELECTED_SAFELIST_LIBRARY_ITEM_CLASS_NAME = 'workflows-safelist-library-item--selected';
const SAFELIST_LIBRARY_ITEM_CONTEXT_MENU_CLASS_NAME = 'workflows-safelist-library-item-context-menu';

const StyledContainer = styled.div`
    display: flex;
    height: 100%;
    min-height: 0;
`;

const StyledScoreFilteringList = styled(TransformationOptionList)`
    flex-basis: 15%;
    margin-right: 2.5rem;
    max-width: 15rem;

`;

const StyledIndicatorTypeList = styled(TransformationOptionList)`
    flex-basis: 52%;
    max-height: 37.333333333rem;
    max-width: 35rem;
    margin-right: 2.5rem;


    & .${INDICATOR_TYPE_LIST_CONTENT_CONTAINER} {
        display: flex;
        flex-flow: column wrap;
        min-height: 0;
    }

    & .${INDICATOR_TYPE_LIST_OPTION_WRAPPER} {
        margin-right: 2.5rem;
    }
`;

const StyledSafelistSummaryList = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 30rem;
    flex-basis: 33%;
`;

const StyledSafelistSummaryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.67rem;
`;

const StyledSafelistSummaryTitleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

const StyledSafelistSummaryTitle = styled.span`
  margin-right: 1rem;
`;

const StyledSafelistSummaryButton = styled(Button)`
    && {
        height: 2.67rem;
        min-height: unset;
        padding: 0 1rem;
        background-color: #d7dae0;
        color: #33373d;
        font-size: 1rem;
        font-weight: 700;
    }
`;

const StyledSafelistSummaryContent = styled(LoadingWrapper)`
    flex: 1;
    min-height: 0;
`;

const StyledSafelistLibraryList = styled(SafelistLibraryList)`
  && {
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 1px;

    .${SAFELIST_LIBRARY_ITEM_CLASS_NAME} {
      background-color: ${colors.interactive.defaultBackground};
    }

    .${SELECTED_SAFELIST_LIBRARY_ITEM_CLASS_NAME} {
      background-color: ${colors.interactive.selectedActive};
    }

    .${SAFELIST_LIBRARY_ITEM_CONTEXT_MENU_CLASS_NAME} {
      background-color: ${colors.background.section};
    }
  }
`;

const StyledSafelistMigrationContent = styled.div`
    align-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const StyledSafelistMigrationMessage = styled.p``;

const TransformationSelection = ({
    values: transformations,
    onChange,
}: TransformationSelectionProps) => {
    const safelistMap = useSelector(getSafelistSummaryMap);
    const dispatch = useDispatch();
    const [safelistGuidToRemove, setSafelistGuidToRemove] = useState('');
    const canManageSafelists = useSelector(canUserManageSafelists);
    const theme: keyof Theme = useSelector(getThemeMode);
    const { loading } = useMutation({ type: MIGRATE_SAFELISTS });

    useEffect(() => {
        if (isEmpty(safelistMap)) {
            dispatch(fetchSafelistSummaries());
        }
    }, []);

    const onAddSafelistSummaryButtonClick = useCallback(() => {
        dispatch(openModal(ADD_SAFELIST_MODAL));
    }, []);

    const onEditSafelistSummaryItem = useCallback((summaryItemGuid: string) => {
        dispatch(startSafelistEditing(summaryItemGuid));
    }, []);

    const onDeleteSafelistSummaryItem = useCallback((safelistGuid) => {
        setSafelistGuidToRemove(safelistGuid);
        dispatch(openModal(REMOVE_SAFELIST_MODAL));
    }, []);

    const onMigrateSafelistButton = useCallback(() => {
        dispatch(migrateSafelists());
    }, []);

    const onDeleteSafelist = (removedSafelistGuid: Safelist['guid']) => {
        if (!isEmpty(transformations.SAFELIST_LIBRARY)) {
            const updatedSafelists = transformations.SAFELIST_LIBRARY?.filter(
                (safelistGuid) => safelistGuid !== removedSafelistGuid
            );

            onChange(updatedSafelists, TransformationType.SAFELIST_LIBRARY);
        }
    };

    return (
        <StyledContainer>
            <StyledScoreFilteringList
                name={TransformationType.SCORE_FILTERING}
                title={'Score Filtering'}
                options={Object.values(ScoreFilteringType)}
                selectedOptions={
                    transformations[TransformationType.SCORE_FILTERING]
                }
                onChange={onChange}
                hasSelectAll={true}
                helpText='Filter on priority score(s) to refine the indicator list that will be sent to your Workflow Application or Enclave.'
            />
            <StyledIndicatorTypeList
                name={TransformationType.INDICATOR_TYPE}
                title={'Indicator Type'}
                options={Object.values(IndicatorType)}
                contentContainerClassName={INDICATOR_TYPE_LIST_CONTENT_CONTAINER}
                optionWrapperClassName={INDICATOR_TYPE_LIST_OPTION_WRAPPER}
                selectedOptions={transformations[TransformationType.INDICATOR_TYPE]}
                onChange={onChange}
                hasSelectAll={true}
                helpText='Filter on Indicator Type(s) to refine the indicator list that will be sent to your Workflow Application or Enclave.'
            />
            <ToggleFeature feature={flags.SHOW_SAFELIST_LIBRARIES} match={treatments.ON}>
                <StyledSafelistSummaryList>
                    <StyledSafelistSummaryHeader>
                        <StyledSafelistSummaryTitleWrapper>
                          <StyledSafelistSummaryTitle>Safelist Libraries</StyledSafelistSummaryTitle>
                          <Tooltip content='Select from your available Safelist Libraries to reduce false positives.'>
                            <Icon icon={IconLookup.QUESTION_CIRCLE} />
                          </Tooltip>
                        </StyledSafelistSummaryTitleWrapper>
                        {canManageSafelists && (
                            <StyledSafelistSummaryButton
                                label="Add Safelist Library"
                                onClick={onAddSafelistSummaryButtonClick}
                            />
                        )}
                    </StyledSafelistSummaryHeader>
                    <StyledSafelistSummaryContent dimOpacityWhenLoading={true} actionToQuery={FETCH_SAFELIST_SUMMARIES}>
                        {!isEmpty(safelistMap)
                            // TODO: We should refactor this to use the same components that we have been using for the rest of the pages.
                            ? <StyledSafelistLibraryList
                                itemCheckboxColor={colorsByTheme[theme].interactive.defaultBorder}
                                itemCheckboxSelectedColor={colorsByTheme[theme].interactive.primaryAction}
                                itemClassName={SAFELIST_LIBRARY_ITEM_CLASS_NAME}
                                selectedItemClassName={SELECTED_SAFELIST_LIBRARY_ITEM_CLASS_NAME}
                                itemContextMenuClassName={SAFELIST_LIBRARY_ITEM_CONTEXT_MENU_CLASS_NAME}
                                safelistSummaries={Object.values(safelistMap)}
                                selectedSummaryGuids={transformations.SAFELIST_LIBRARY}
                                onEditSummaryItem={onEditSafelistSummaryItem}
                                onDeleteSummaryItem={onDeleteSafelistSummaryItem}
                                onSelect={(selectedLibraries) => (
                                    onChange(selectedLibraries, TransformationType.SAFELIST_LIBRARY)
                                )}
                                showContextMenu={canManageSafelists}
                              />
                            : <StyledSafelistMigrationContent>
                                <StyledSafelistMigrationMessage>
                                    Migrate existing company whitelist as your default Workflow Safelist.
                                </StyledSafelistMigrationMessage>
                                <Button
                                  label="Migrate safelists"
                                  isLoading={loading}
                                  onClick={onMigrateSafelistButton}
                                />
                              </StyledSafelistMigrationContent>
                        }
                    </StyledSafelistSummaryContent>
                </StyledSafelistSummaryList>
            </ToggleFeature>
            {canManageSafelists && <AddSafelistModal />}
            {canManageSafelists && !isEmpty(safelistMap) && <EditSafelistModal />}
            {canManageSafelists && safelistGuidToRemove && (
                <RemoveSafelistModal
                    safelistGuid={safelistGuidToRemove}
                    onRemove={onDeleteSafelist}
                />
            )}
        </StyledContainer>
    );
}

export default TransformationSelection;
